import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, Form } from 'redux-form';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { withRouter } from 'react-router-dom';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import {
  getProductPricePreview,
  resetProductPreview
} from 'menus/actions/productPreview';
import { getIsCartTotalExceedMaximum } from 'cart/actions/orderAmount';
import { buildMods } from 'menus/utils/cartModifications';
import { isMobileMedia } from 'utils/media';

import {
  ORDER_MADE_FOR_LENGTH,
  SPECIAL_INSTRUCTIONS_LENGTH
} from 'menus/constants/inputSize';

import MultiLineTextAreaField from 'menus/components/MultiLineTextAreaField';
import TextInputField from 'menus/components/TextInputField';
import QuantityInputField from 'menus/components/QuantityInputField';
import MenuItemDetailPrice from 'menus/components/MenuItemDetailPrice';
import VariantGroupForm from 'menus/components/VariantGroupForm';
import { getIsEnabledAddToCartSelector } from 'menus/selectors';
import { resetError } from 'cart/actions/';
import MenuItemSubmitContainer from 'menus/containers/MenuItemSubmitContainer';
import { MENU_ITEM, CART_ITEM } from 'menus/constants/formTypes';

// util to compare previous and next props
// param prev obj
// param updated obj
// returns string of key name that has changed
// returns empty string if no change has occurred
// only one key will be returned
// only useful where values are flat primitives (integers, strings)
// could use lodash deepEqual if properties point to objects
const whichFieldChanged = (prev, updated) =>
  Object.keys(updated).reduce((acc, currKey) => {
    const updatedValue = updated[currKey];
    const prevValue = prev[currKey];
    if (updatedValue !== prevValue) {
      return currKey;
    }
    return acc;
  }, '');

class MenuItemDetailFormWrapper extends React.Component {
  static propTypes = {
    handleSubmit: PropTypes.func,
    itemForm: PropTypes.object,
    dispatch: PropTypes.func,
    variants: PropTypes.array,
    defaultCtaName: PropTypes.string,
    preSelectedFields: PropTypes.object,
    initialize: PropTypes.func,
    isMaximumExceeded: PropTypes.bool,
    cartItemId: PropTypes.string,
    orderMaximum: PropTypes.string,
    productId: PropTypes.number,
    productName: PropTypes.string,
    pricePreview: PropTypes.string,
    inventoryIsDepletedWithCurrentQuantity: PropTypes.bool,
    previewedQuantityExceedsInventory: PropTypes.bool,
    inventory: PropTypes.number,
    inventoryLessCart: PropTypes.number,
    cartErrors: PropTypes.arrayOf(PropTypes.string),
    qtyCurrentlyInCart: PropTypes.number,
    isUpdatingPreview: PropTypes.bool,
    formType: PropTypes.oneOf([MENU_ITEM, CART_ITEM]),
    requestedQty: PropTypes.number
  };

  static defaultProps = {
    cartErrors: [],
    formType: MENU_ITEM
  };

  componentDidMount() {
    const { preSelectedFields, initialize } = this.props;
    if (preSelectedFields && !isEmpty(preSelectedFields)) {
      initialize(preSelectedFields);
    }
    this.calculatePricePreview();
  }

  componentWillUnmount() {
    this.props.dispatch(resetError());
    this.props.dispatch(resetProductPreview());
  }

  /**
   * Set the initial cost of the item and any additional cost update
   */
  componentDidUpdate(prevProps) {
    const {
      preSelectedFields,
      initialize,
      dispatch,
      cartItemId,
      productId
    } = this.props;
    /**
     * calculate whether we exceed maximum whenever the price updates
     */
    if (prevProps.pricePreview !== this.props.pricePreview) {
      dispatch(getIsCartTotalExceedMaximum(cartItemId));
    }

    /* initial calculation for price preview once menu item is available */
    if (prevProps.productId !== productId) {
      this.calculatePricePreview();
      dispatch(resetError());
    }

    /**
     * Prepopulate the menu item form when user wants to edit cart item
     */
    if (
      isEmpty(prevProps.preSelectedFields) &&
      prevProps.preSelectedFields !== preSelectedFields
    ) {
      initialize(preSelectedFields);
    }

    /* check to see if cta should be clickable. We only calculate the price if
    user make updates to fields other than text inputs */

    if (this.shouldRecalculatePrice(prevProps)) {
      this.calculatePricePreview();
      dispatch(resetError());
    }
  }

  shouldRecalculatePrice(prevProps) {
    const { itemForm, productId } = this.props;
    const currentValues = get(itemForm, 'menuItemDetail.values', {});
    const previousValues = get(prevProps, 'itemForm.menuItemDetail.values', {});

    const nameOfFieldChanged = whichFieldChanged(previousValues, currentValues);

    const shouldRecalculatePrice =
      productId &&
      nameOfFieldChanged &&
      !['order-made-for', 'specialInstructions'].includes(nameOfFieldChanged);

    return shouldRecalculatePrice;
  }

  /**
   * Calculate the cost of a menu item base on the variants selected
   * and the total quantity.
   */
  calculatePricePreview = () => {
    const { dispatch, itemForm, productId } = this.props;
    const values = get(itemForm, 'menuItemDetail.values');
    let payload = {};
    if (values) {
      const { quantityInput, ...modifications } = values;
      payload = {
        product_id: productId,
        quantity: quantityInput,
        required_fields: {
          ...buildMods(modifications)
        }
      };
    }

    dispatch(getProductPricePreview(productId, payload));
  };

  render() {
    const {
      variants,
      itemForm,
      defaultCtaName,
      isMaximumExceeded,
      orderMaximum,
      // curr inventory for an item (doesn't take into account cart items)
      inventory,
      inventoryIsDepletedWithCurrentQuantity,
      previewedQuantityExceedsInventory,
      pricePreview,
      productName,
      cartErrors,
      qtyCurrentlyInCart,
      // excludes requested quantity from preview
      inventoryLessCart,
      isUpdatingPreview,
      formType,
      requestedQty
    } = this.props;

    const hasCartError = !!cartErrors.length;
    const shouldDisplayInventoryLeft =
      !!inventoryLessCart &&
      inventoryLessCart > 0 &&
      inventoryLessCart < 5 &&
      !previewedQuantityExceedsInventory;

    const shouldDisplayAddableInventory =
      !!inventoryLessCart &&
      inventoryLessCart > 0 &&
      !hasCartError &&
      (inventoryIsDepletedWithCurrentQuantity ||
        previewedQuantityExceedsInventory);
    return (
      <Form onSubmit={this.props.handleSubmit}>
        <VariantGroupForm variants={variants} form={itemForm} />
        <Box width="100%" borderBottom="1px solid" color="borders.primary">
          <Field
            name="order-made-for"
            placeholder="Order made for"
            dataTestId="menu-item-detail-order-made-for"
            component={TextInputField}
            maxLength={ORDER_MADE_FOR_LENGTH}
          />
          <Field
            name="specialInstructions"
            placeholder="Special Instructions"
            dataTestId="menu-item-detail-special-instructions"
            component={MultiLineTextAreaField}
            maxLength={SPECIAL_INSTRUCTIONS_LENGTH}
          />
        </Box>
        <Box mt={2.5} mb={2.5} display="flex">
          <Field
            name="quantityInput"
            component={QuantityInputField}
            disableDecrement={requestedQty === 1 || isUpdatingPreview}
            disableIncrement={
              isMaximumExceeded ||
              previewedQuantityExceedsInventory ||
              inventoryIsDepletedWithCurrentQuantity ||
              isUpdatingPreview
            }
          />

          {qtyCurrentlyInCart > 0 && qtyCurrentlyInCart === inventory && (
            <Box ml={1}>
              <Typography variant="body1">{`Last ${qtyCurrentlyInCart} in cart`}</Typography>
            </Box>
          )}
          {/* don't display if inventoryLessCart get to zero, that indicates sold out */}
          {shouldDisplayInventoryLeft && (
            <Box ml={1}>
              <Typography
                data-testid="menu-item-detail-inventory-left"
                variant="body1"
              >{`Only ${inventoryLessCart} left`}</Typography>
            </Box>
          )}
          <MenuItemDetailPrice cost={pricePreview} />
        </Box>
        {isMaximumExceeded && (
          <Box mb={2.5}>
            <Typography variant="caption">
              {`You’ve reached the order maximum of $${orderMaximum}. Please
                remove items from your cart or adjust their quantities to
                continue to checkout.`}
            </Typography>
          </Box>
        )}
        {shouldDisplayAddableInventory && (
          <Box mb={2.5}>
            <Typography
              data-testid="menu-item-detail-limited-quantity-caption"
              variant="caption"
            >
              {`We have a limited quantity of ${productName} and only ${inventoryLessCart} can be added to your cart.`}
            </Typography>
          </Box>
        )}
        {/* the cartErrors object check is used to distinguish cart error from add to cart errors */}
        {hasCartError && typeof cartErrors[0] !== 'object' && (
          <Box mb={2.5}>
            <Typography variant="caption">{cartErrors[0]}</Typography>
          </Box>
        )}
        {!isMobileMedia() && (
          <MenuItemSubmitContainer
            defaultCtaName={defaultCtaName}
            formType={formType}
          />
        )}
      </Form>
    );
  }
}

const mapStateToProps = state => {
  return {
    isAddingToCart: state.cart.isFetching,
    cartErrors: state.cart.error,
    itemForm: state.form,
    initialValues: state.editCart,
    account: state.account,
    ...getIsEnabledAddToCartSelector(state)
  };
};

/**
 * dispatching submit action when user submits redux form.
 * values, dispatch, props is coming from redux form.
 * see: https://redux-form.com/8.2.2/docs/api/reduxform.md/#-code-onsubmit-function-code-optional-
 */
let MenuItemDetailForm = reduxForm({
  form: 'menuItemDetail',
  onSubmit: (values, dispatch, props) =>
    dispatch(props.handleFormItemSubmit(values, props))
})(MenuItemDetailFormWrapper);

MenuItemDetailForm = withRouter(MenuItemDetailForm);

export default connect(mapStateToProps)(MenuItemDetailForm);
